import { Fragment, useCallback, useEffect } from "react";

import {
  Aggregator,
  type CurrencyCode,
  CurrencyCodes,
  Feature,
  Metric,
  Renderer,
  TimeInterval,
} from "@doitintl/cmp-models";
import { Checkbox, MenuItem, TextField, Tooltip } from "@mui/material";

import { reportText } from "../../../../assets/texts";
import { Currency } from "../../../../Components/Currency/Currency";
import { useAuthContext } from "../../../../Context/AuthContext";
import { useIsFeatureEntitled } from "../../../../Context/TierProvider";
import { useReportConfig, useReportDimensionsContext } from "../../Context";
import { useStyles } from "../../ReportStyles";
import {
  advancedOptionsRenderValue,
  advancedOptionsTitle,
  AggregatorOptions,
  FeatureOptions,
  getAvailableAggregators,
  isComparative,
  isTimeSeriesReport,
  IsValidAggregator,
  MenuProps,
} from "../../utilities";
import { IncludeCreditsSwitch } from "./IncludeCreditsSwitch";
import ReportsAccordion from "./ReportsAccordion";

export type AdvancedOptionsProps = {
  handleAggregatorSelect: (aggregator: Aggregator) => void;
  queryRunning: boolean;
  handleCurrencyChange: (newCurrency: CurrencyCode) => void;
};

const AdvancedOptions = ({ handleAggregatorSelect, queryRunning, handleCurrencyChange }: AdvancedOptionsProps) => {
  const { isDoitEmployee } = useAuthContext({ mustHaveUser: true });
  const isEntitledAnalyticsAdvanced = useIsFeatureEntitled("analytics:advanced");
  const classes = useStyles();
  const { dimensions = [] } = useReportDimensionsContext();
  const { reportConfig, dispatchReportConfig } = useReportConfig();
  const { currency, aggregator, renderer, features, timeInterval, metric, comparative } = reportConfig;

  const onChange = useCallback(
    (event) => {
      dispatchReportConfig({ payload: { features: event.target.value } });
    },
    [dispatchReportConfig]
  );

  const onChangeAggregation = useCallback(
    (event) => {
      handleAggregatorSelect(event.target.value as Aggregator);
    },
    [handleAggregatorSelect]
  );

  useEffect(() => {
    if (aggregator === Aggregator.TOTAL_OVER_TOTAL && (metric === Metric.EXTENDED || metric === Metric.CALCULATED)) {
      dispatchReportConfig({ payload: { aggregator: Aggregator.TOTAL } });
    }
  }, [metric, aggregator, dispatchReportConfig]);

  return (
    <ReportsAccordion title={reportText.ADVANCED_OPTIONS_SECTION_LABEL} numOfItems={3}>
      <Fragment>
        {(isDoitEmployee || isEntitledAnalyticsAdvanced) && (
          <Tooltip title={advancedOptionsTitle(dimensions, timeInterval, features)} placement="bottom-start">
            <TextField
              label="Advanced Analysis"
              data-cy="advanced-analysis"
              value={features}
              disabled={!isTimeSeriesReport(dimensions, timeInterval)}
              onChange={onChange}
              variant="outlined"
              margin="dense"
              select
              fullWidth
              slotProps={{
                select: {
                  multiple: true,
                  displayEmpty: true,
                  renderValue: (values) => advancedOptionsRenderValue(dimensions, values as Feature[], timeInterval),
                  classes: {
                    select: classes.selectOutlined,
                  },
                  MenuProps: {
                    ...MenuProps,
                    PopoverClasses: {
                      root: classes.customMenu,
                    },
                  },
                },

                inputLabel: { shrink: true, margin: "dense" },
              }}
            >
              {FeatureOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  disabled={
                    (timeInterval === TimeInterval.YEAR || aggregator !== Aggregator.TOTAL) &&
                    option.value === Feature.FORECAST
                  }
                  dense
                  data-cy={option.value}
                >
                  <Checkbox size="small" checked={features?.includes(option.value)} />
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Tooltip>
        )}
      </Fragment>
      <TextField
        label="Aggregation"
        data-cy="aggregation"
        value={aggregator}
        onChange={onChangeAggregation}
        variant="outlined"
        disabled={renderer === Renderer.TREEMAP || !!(comparative && isComparative(comparative))}
        margin="dense"
        select
        fullWidth
        slotProps={{
          select: {
            classes: {
              select: classes.selectOutlined,
            },
            MenuProps,
          },
        }}
      >
        {getAvailableAggregators(AggregatorOptions, aggregator, metric).map((aggregatorOption) => (
          <MenuItem
            key={aggregatorOption.value}
            value={aggregatorOption.value}
            disabled={!IsValidAggregator(aggregatorOption.value, metric)}
            dense
            data-cy={
              typeof aggregatorOption.label === "function" ? aggregatorOption.label(metric) : aggregatorOption.label
            }
          >
            {typeof aggregatorOption.label === "function" ? aggregatorOption.label(metric) : aggregatorOption.label}
          </MenuItem>
        ))}
      </TextField>
      <Currency value={currency ?? CurrencyCodes.USD} onChange={handleCurrencyChange} />
      <IncludeCreditsSwitch disabled={queryRunning} />
    </ReportsAccordion>
  );
};
export default AdvancedOptions;
